/**
 * Toast notification context and provider
 * Provides a centralized way to show toast notifications throughout the app
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import Toast from '../components/ui/toast';
import {
  DEFAULT_USER_FACING_ERROR_MESSAGE,
  toUserFacingErrorMessage,
} from '../utils/userFacingErrorMessage';
import { registerDevWarningHandler } from '../utils/devDiagnosticToast';

const DEFAULT_MAX_TOASTS = 3;

export interface ToastMessage {
  id: string;
  resetKey: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export type ToastInput = Omit<ToastMessage, 'id' | 'resetKey'>;

interface ToastContextValue {
  showToast: (message: ToastInput) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  hideToast: (id: string) => void;
  hideAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/** Mark toast stack so modal outside-click handlers can ignore interactions here. */
export function isTargetInsideDocufyToastRegion(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false;
  const root = document.querySelector('[data-docufy-toast-region]');
  return !!root?.contains(target);
}

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = DEFAULT_MAX_TOASTS,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const generateId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `toast-${crypto.randomUUID()}`;
    }
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return `toast-${array[0].toString(36)}`;
    }
    // Final fallback (no Math.random)
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return `toast-${Date.now().toString(36)}-${performance.now().toString(36)}`;
    } else {
      return `toast-${Date.now().toString(36)}`;
    }
  }, []);

  const showToast = useCallback(
    (toast: ToastInput) => {
      const resetKey = generateId();
      const message =
        toast.type === 'error'
          ? toUserFacingErrorMessage(toast.message, DEFAULT_USER_FACING_ERROR_MESSAGE)
          : toast.message;
      const newToast: ToastMessage = { ...toast, id: resetKey, resetKey, message };

      setToasts((prev) => {
        const duplicateIndex = prev.findIndex(
          (existingToast) =>
            existingToast.type === newToast.type && existingToast.message === newToast.message,
        );
        if (duplicateIndex >= 0) {
          const existingToast = prev[duplicateIndex];
          const bumpedToast = { ...newToast, id: existingToast.id };
          const previousWithoutDuplicate = prev.filter((_, index) => index !== duplicateIndex);
          return [bumpedToast, ...previousWithoutDuplicate].slice(0, maxToasts);
        }

        return [newToast, ...prev].slice(0, maxToasts);
      });
    },
    [generateId, maxToasts],
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'success', duration });
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'error', duration: duration || 8000 }); // Longer duration for errors
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'warning', duration: duration || 6000 });
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'info', duration });
    },
    [showToast],
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextValue = useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      hideToast,
      hideAllToasts,
    }),
    [showToast, showSuccess, showError, showWarning, showInfo, hideToast, hideAllToasts],
  );

  useEffect(() => {
    registerDevWarningHandler(showWarning);
    return () => registerDevWarningHandler(null);
  }, [showWarning]);

  useEffect(() => {
    const handleSelectionChange = () => setSelectionVersion((version) => version + 1);
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Toast container - positioned at top center */}
      {/* Use z-[100] to ensure toasts appear above modals (which use z-50) */}
      <div
        data-docufy-toast-region
        className='fixed top-16 left-1/2 transform -translate-x-1/2 z-[100] space-y-2 pointer-events-none'
      >
        {toasts.map((toast) => (
          <div key={toast.id} className='pointer-events-auto'>
            <Toast
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              resetKey={toast.resetKey}
              selectionVersion={selectionVersion}
              action={toast.action}
              onClose={() => hideToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
